import {FC} from 'react';
import { Col } from 'antd';

import ToggleBox from '~src/shared/form/toggleBox/ToggleBox';
import Checkbox from '~src/shared/form/Checkbox';
import SvgIcon from '~src/shared/SvgIcon';
import { ScheduleI } from '~src/interfaces/schedule.interface';
import { SurveyUseCase } from '~src/interfaces/surveys.interfaces';
import { SurveyScopes } from '~src/modules/surveys/interfaces';

import { getModalTexts, modalBody } from './ToggleBoxSection';
import {
  PeopleSectionHeader,
  SecondPeopleSectionBottom,
  SectionPart,
  SelectionSubtitle,
  SelectionWrapper,
  StyledRow,
  ToggleWrapper,
} from './styles/SectionStyles';
import {useTranslation} from "react-i18next";

interface Props {
  title: string;
  titleKey: string;
  toggleBoxTitle: string;
  withToggleBox: boolean;
  onChangeToggleBox: (active: boolean) => void;
  active: boolean;
  schedule: ScheduleI;
  oldSchedule: ScheduleI;
  subject: string;
  useCase: SurveyUseCase;
  changeFlag: (value: boolean, key: keyof ScheduleI) => void;
  scope?: SurveyScopes;
  locked?: boolean;
}

export const CheckBoxSection: FC<Props> = ({
  changeFlag,
  locked,
  schedule,
  useCase,
  oldSchedule,
  toggleBoxTitle = '',
  withToggleBox = false,
  active = false,
  onChangeToggleBox,
  scope,
  title,
  titleKey,
}: Props)=> {

  const { t } = useTranslation();

  const changeSelfAssessment = (value: boolean) => {
    changeFlag(value, 'selfAssessment');
  };

  const  changeSelfSelection = (value: boolean) => {
    changeFlag(value, 'selfSelection');
  };

  const changeManagersSelection = (value: boolean) => {
    changeFlag(value, 'managersSelection');
  };

  const changeAskManagersOfAbouts = (value: boolean): void => {
    changeFlag(value, 'askManagersOfAbouts');
  };

  const askSectionKey = 'x.surveys.schedule.asksection';

  return (
    <PeopleSectionHeader>
      <StyledRow>
        {!locked && (
          <Col span={12}>
            {withToggleBox && (
              <SectionPart>
                <SecondPeopleSectionBottom>
                  <SvgIcon name="iconAnonymous" />
                  <span style={{ textAlign: 'center' }}>{toggleBoxTitle}</span>
                  <ToggleWrapper>
                    <ToggleBox
                      renderModelBody={modalBody}
                      modalTexts={getModalTexts(t)}
                      showModalOnChange={!oldSchedule.anonymous && oldSchedule.wasStarted}
                      popoverMessage={{
                        key: `${askSectionKey}.togglebox.locked_message`,
                        txt: t(`${askSectionKey}.togglebox.locked_message`),
                      }}
                      locked={oldSchedule.anonymous && oldSchedule.wasStarted}
                      active={active}
                      onChangeBox={onChangeToggleBox}
                      title={[t(`${askSectionKey}.togglebox.yes`), t(`${askSectionKey}.togglebox.no`)]}
                    />
                  </ToggleWrapper>
                </SecondPeopleSectionBottom>
              </SectionPart>
            )}
          </Col>
        )}
        <Col span={locked ? 24 : 12}>
          <SectionPart>
            <SelectionWrapper>
              {(useCase === SurveyUseCase.BottomUp || useCase === SurveyUseCase.TopDown) && (
                <>
                  <Checkbox
                    id="managers_selection"
                    label={t<string>(`${askSectionKey}.checkbox.managers_selection.${useCase}`)}
                    labelKey={`${askSectionKey}.checkbox.managers_selection.${useCase}`}
                    isChecked={schedule.managersSelection}
                    onChange={changeManagersSelection}
                  />
                  <SelectionSubtitle
                    data-translation={`${askSectionKey}.checkbox.managers_selection.subtitle.${useCase}`}
                  >
                    {t(`${askSectionKey}.checkbox.managers_selection.subtitle.${useCase}`)}
                  </SelectionSubtitle>
                  <Checkbox
                    id="self_selection"
                    label={t<string>(`${askSectionKey}.checkbox.self_selection.${useCase}`)}
                    labelKey={`${askSectionKey}.checkbox.self_selection.${useCase}`}
                    isChecked={schedule.selfSelection}
                    onChange={changeSelfSelection}
                  />
                  <SelectionSubtitle
                    data-translation={`${askSectionKey}.checkbox.self_selection.subtitle.${useCase}`}
                  >
                    {t(`${askSectionKey}.checkbox.self_selection.subtitle.${useCase}`)}
                  </SelectionSubtitle>
                </>
              )}
              {useCase === SurveyUseCase.BottomUp && (
                <>
                  <Checkbox
                    id="ask_managers_of_abouts"
                    label={t(
                      `x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.${useCase}.${scope}`,
                    )}
                    labelKey={`x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.${useCase}.${scope}`}
                    isChecked={schedule.askManagersOfAbouts}
                    onChange={changeAskManagersOfAbouts}
                  />
                  <SelectionSubtitle
                    data-translation={`x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.subtitle.${useCase}.${scope}`}
                  >
                    {t(
                      `x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.subtitle.${useCase}.${scope}`,
                    )}
                  </SelectionSubtitle>
                </>
              )}
              <Checkbox
                id="askPeopleThemselves"
                label={title}
                labelKey={titleKey}
                isChecked={schedule.selfAssessment}
                onChange={changeSelfAssessment}
              />
              <SelectionSubtitle
                data-translation={`${askSectionKey}.checkbox.self_assessment.subtitle.${useCase}${
                  useCase === SurveyUseCase.ThreeSixty ? `.${scope}` : ''
                }`}
              >
                {t(
                  `${askSectionKey}.checkbox.self_assessment.subtitle.${useCase}${
                    useCase === SurveyUseCase.ThreeSixty ? `.${scope}` : ''
                  }`,
                )}
              </SelectionSubtitle>
            </SelectionWrapper>
          </SectionPart>
        </Col>
      </StyledRow>
    </PeopleSectionHeader>
  );
}

export default CheckBoxSection;
